import { useSearchParams } from "react-router-dom";
import { useTasks } from "../../app/hooks";
import {
  ResourceDiscriminants,
  TaskKindDiscriminants,
  TaskListKind,
  Tool,
} from "../../app/backendTypes";
import { useState } from "react";
import Loading from "../common/Loading";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import TaskView from "../common/tasks/TaskView";
import Error from "../common/Error";

export default function Tasks() {
  const [params, setParams] = useSearchParams();

  const paramListKindValue = params.get("listKind");

  const paramListKind =
    paramListKindValue !== null && paramListKindValue in TaskListKind
      ? (paramListKindValue as TaskListKind)
      : null;

  const [listKind, setListKind] = useState(
    paramListKind || TaskListKind.Active
  );

  const paramTaskKindValue = params.get("taskKind");

  const paramTaskKind =
    paramTaskKindValue !== null && paramTaskKindValue in TaskKindDiscriminants
      ? (paramTaskKindValue as TaskKindDiscriminants)
      : null;

  const [taskKind, setTaskKind] = useState(
    listKind === "Error"
      ? null
      : paramTaskKind || TaskKindDiscriminants.ToolAccess
  );

  const paramTaskSubkind = params.get("taskSubkind");

  const [taskSubkind, setTaskSubkind] = useState(
    taskKind === "ToolAccess"
      ? paramTaskSubkind !== null && paramTaskSubkind in Tool
        ? paramTaskSubkind
        : Tool.Jira
      : taskKind === "SupportTicket"
      ? paramTaskSubkind !== null && paramTaskSubkind in ResourceDiscriminants
        ? paramTaskSubkind
        : ResourceDiscriminants.Catalog
      : null
  );

  const { tasks, error, loading, clear } = useTasks(
    listKind,
    taskKind,
    taskSubkind
  );

  const handleListKindChange = (event: SelectChangeEvent) => {
    const listKind = event.target.value as TaskListKind;

    clear();

    setListKind(listKind);

    setTaskKind(
      listKind === "Error" ? null : taskKind || TaskKindDiscriminants.ToolAccess
    );

    setTaskSubkind(
      taskKind === "ToolAccess" || taskKind === null
        ? taskSubkind !== null && taskSubkind in Tool
          ? taskSubkind
          : Tool.Jira
        : taskKind === "SupportTicket"
        ? taskSubkind !== null && taskSubkind in ResourceDiscriminants
          ? taskSubkind
          : ResourceDiscriminants.Contract
        : null
    );

    setParams({
      listKind,
      taskKind: listKind === "Error" ? "" : taskKind || "ToolAccess",
      taskSubkind:
        taskKind === "ToolAccess"
          ? taskSubkind !== null && taskSubkind in Tool
            ? taskSubkind
            : "Jira"
          : taskKind === "SupportTicket"
          ? taskSubkind !== null && taskSubkind in ResourceDiscriminants
            ? taskSubkind
            : "Contract"
          : "",
    });
  };

  const handleTaskKindChange = (event: SelectChangeEvent) => {
    const taskKind = event.target.value as TaskKindDiscriminants;

    clear();

    setTaskKind(taskKind);

    setTaskSubkind(
      taskKind === "ToolAccess"
        ? taskSubkind !== null && taskSubkind in Tool
          ? taskKind
          : Tool.Jira
        : taskKind === "SupportTicket"
        ? taskSubkind !== null && taskSubkind in ResourceDiscriminants
          ? taskSubkind
          : ResourceDiscriminants.Contract
        : null
    );

    setParams({
      listKind,
      taskKind,
      taskSubkind:
        taskKind === "ToolAccess"
          ? taskSubkind !== null && taskSubkind in Tool
            ? taskSubkind
            : "Jira"
          : taskKind === "SupportTicket"
          ? taskSubkind !== null && taskSubkind in ResourceDiscriminants
            ? taskSubkind
            : "Contract"
          : "",
    });
  };

  const handleTaskSubkindChange = (event: SelectChangeEvent) => {
    const subkind = event.target.value;

    clear();
    setTaskSubkind(subkind);
    setParams({
      listKind,
      taskKind: taskKind || "ToolAccess",
      taskSubkind: subkind,
    });
  };

  let comp = <></>;
  if (loading) comp = <Loading />;
  else if (error) comp = <Error error={error} />;
  else
    comp = (
      <>
        <Typography variant="h6">Found {tasks.length} tasks</Typography>

        {tasks.map((t, i) => (
          <TaskView
            key={i}
            task={t}
            archived={listKind === TaskListKind.Archived}
          />
        ))}
      </>
    );

  return (
    <>
      <FormControl fullWidth size="small" sx={{ maxWidth: "sm" }}>
        <InputLabel id="list-kind">List Kind</InputLabel>
        <Select
          labelId="list-kind"
          value={listKind}
          label="List Kind"
          onChange={handleListKindChange}
        >
          {Object.keys(TaskListKind).map((k, i) => (
            <MenuItem key={i} value={k}>
              {k}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {listKind !== "Error" && (
        <FormControl fullWidth size="small" sx={{ maxWidth: "sm" }}>
          <InputLabel id="task-kind">Task Kind</InputLabel>
          <Select
            labelId="task-kind"
            value={taskKind || ""}
            label="Task Kind"
            onChange={handleTaskKindChange}
          >
            {Object.keys(TaskKindDiscriminants).map((k, i) => (
              <MenuItem key={i} value={k}>
                {k}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {listKind !== "Error" &&
        (taskKind === "ToolAccess" || taskKind === "SupportTicket") && (
          <FormControl fullWidth size="small" sx={{ maxWidth: "sm" }}>
            <InputLabel id="subkind">
              {taskKind === "ToolAccess"
                ? "Tool"
                : taskKind === "SupportTicket"
                ? "Resource"
                : "Subkind"}
            </InputLabel>
            <Select
              labelId="subkind"
              value={taskSubkind || ""}
              label={
                taskKind === "ToolAccess"
                  ? "Tool"
                  : taskKind === "SupportTicket"
                  ? "Resource"
                  : "Subkind"
              }
              onChange={handleTaskSubkindChange}
            >
              {taskKind === "ToolAccess" &&
                Object.keys(Tool).map((k, i) => (
                  <MenuItem key={i} value={k}>
                    {k}
                  </MenuItem>
                ))}

              {taskKind === "SupportTicket" &&
                Object.keys(ResourceDiscriminants).map((k, i) => (
                  <MenuItem key={i} value={k}>
                    {k}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

      {comp}
    </>
  );
}
